// A session's transcript: its items in order, a run of actions folded into
// one row, and the end kept in view while he reads it. Once he scrolls up,
// or opens something by hand, the page stops pulling to the end and offers
// "Jump to latest". Moved from today's Code tab (code/Transcript.tsx
// there).
//
// A long transcript draws its last WINDOW blocks first (Poulpe-843): a
// session of thousands of replies is readable, and its prompt usable, at
// once. "Show earlier", or scrolling near the top, draws WINDOW more above,
// and what he reads stays where it is. Once he leaves the end, the first
// block drawn stays the first, so what comes in at the end, or older items
// filled in above, moves nothing he reads.
//
// A live region reads out the agent's text as each reply is complete.
//
// An expired question shows where it was asked (questions/expired.ts).
import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import type { Me, Question } from '../api/types'
import type { AskRecord } from '../questions/asks'
import { END, placeExpired } from '../questions/expired'
import { Working } from '../components/Working'
import { renderMarkdown } from '../markdown/render'
import type { TranscriptView } from '../sessions/follow'
import { ChevronDownIcon } from './icons'
import { lastReplies, toBlocks, toRows, type Block, type Row } from './items'
import { ActionGroup, AskRecords, ItemView, TranscriptContext, type TranscriptActions } from './TranscriptItems'
import type { AssistantItem, Item, UserItem } from './types'

/** How near its end a transcript must be scrolled for the page to keep following it. */
const FOLLOW_PX = 48

/** How many blocks are drawn at first, and how many more each time he goes back. */
export const WINDOW = 150

/** How near its top a transcript must be scrolled to draw more above. */
const EARLIER_PX = 400

/** A block's key: its first item's id. */
function keyOf(block: Block): string {
  return block.kind === 'group' ? `group-${block.rows[0]!.item.id}` : block.row.item.id
}

export interface TranscriptProps {
  view: TranscriptView
  me: Me | null
  /** The session runs: its last text may still be written, and its last reply is not yet known. */
  busy?: boolean
  /** Told whether he reads the latest. */
  onFollowing?: (following: boolean) => void
  /** What a prompt that carried an answer to the session's question shows instead: the question and its answer, when known. */
  askRecord?: (item: UserItem) => AskRecord | null
  /** Given, a shell block in a reply has Run, which calls this with its command. */
  onRun?: (command: string) => void
  /** The session's expired questions, shown where they were asked: when each was asked, when known, and how each shows. */
  expired?: ExpiredQuestions
}

export interface ExpiredQuestions {
  questions: readonly Question[]
  askedAt: (id: string) => string | null
  render: (question: Question) => ReactNode
}

function atEnd(box: HTMLElement): boolean {
  return box.scrollHeight - box.scrollTop - box.clientHeight <= FOLLOW_PX
}

/** The rows of the items; a row whose item and calls did not change is the object it was. */
function useRows(items: readonly Item[]): Row[] {
  const known = useRef(new WeakMap<Item, Row>())
  return useMemo(() => {
    const stable = (row: Row): Row => {
      const children = row.children.map(stable)
      const before = known.current.get(row.item)
      if (before && before.children.length === children.length && before.children.every((child, i) => child === children[i])) return before
      const next = { item: row.item, children }
      known.current.set(row.item, next)
      return next
    }
    return toRows(items).map(stable)
  }, [items])
}

export function Transcript({ view, me, busy = false, onFollowing, askRecord, onRun, expired }: TranscriptProps) {
  const { items, status, error, cwd } = view
  const box = useRef<HTMLDivElement>(null)
  const content = useRef<HTMLDivElement>(null)
  const following = useRef(true)
  /** The height of what scrolls, as last seen: where its end was before it grew. */
  const lastHeight = useRef(0)
  const [away, setAway] = useState(false)
  const rows = useRows(items)
  const blocks = useMemo(() => toBlocks(rows), [rows])
  /** The first block drawn, by key, once he left the end or went back; null draws the last WINDOW. */
  const [fromKey, setFromKey] = useState<string | null>(null)
  const anchored = fromKey === null ? -1 : blocks.findIndex((block) => keyOf(block) === fromKey)
  const start = anchored >= 0 ? anchored : Math.max(0, blocks.length - WINDOW)
  /** The height of what scrolls before drawing more above, to keep what he reads in place. */
  const heightBefore = useRef<number | null>(null)
  const last = useMemo(() => lastReplies(items, busy), [items, busy])
  const said = useNewText(items, status === 'ready')
  const placed = useMemo(() => (expired && expired.questions.length > 0 ? placeExpired(blocks, expired.questions, expired.askedAt) : null), [blocks, expired])
  const after = (at: number) => placed?.get(at)?.map((question) => <Fragment key={`expired-${question.id}`}>{expired!.render(question)}</Fragment>)

  /** The first block drawn now: kept first once he leaves the end. */
  const firstKey = blocks[start] ? keyOf(blocks[start]) : null
  const firstKeyRef = useRef(firstKey)
  firstKeyRef.current = firstKey

  const follow = useCallback(
    (next: boolean) => {
      if (following.current !== next) onFollowing?.(next)
      following.current = next
      setAway(!next)
      if (!next) setFromKey((key) => key ?? firstKeyRef.current)
    },
    [onFollowing],
  )

  /** Draw WINDOW more blocks above, keeping what he reads where it is. */
  const earlier = useCallback(() => {
    if (start === 0) return
    heightBefore.current = box.current?.scrollHeight ?? null
    setFromKey(keyOf(blocks[Math.max(0, start - WINDOW)]!))
  }, [blocks, start])

  // After drawing more above: what he read moves down by what was added.
  useLayoutEffect(() => {
    const el = box.current
    const before = heightBefore.current
    heightBefore.current = null
    if (!el || before === null) return
    el.scrollTop += el.scrollHeight - before
    lastHeight.current = el.scrollHeight
  }, [start])

  /**
   * Keep the end in view while he follows it. Where he is decides, against
   * the height last seen: he may have scrolled up a moment ago, with that
   * scroll's event still on its way, and what he went to read must not be
   * pulled from under him.
   */
  const pin = useCallback(() => {
    const el = box.current
    if (!el) return
    if (following.current) {
      if (lastHeight.current - el.scrollTop - el.clientHeight <= FOLLOW_PX) el.scrollTop = el.scrollHeight
      else follow(false)
    }
    lastHeight.current = el.scrollHeight
  }, [follow])

  // After every change: stay at the end while following.
  useLayoutEffect(() => {
    pin()
  }, [items, status, pin])

  // What grows without a change of items (a long text drawn a moment later) keeps the end in view too.
  useEffect(() => {
    const inner = content.current
    if (!inner || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(pin)
    observer.observe(inner)
    return () => observer.disconnect()
  }, [status, pin])

  const onScroll = () => {
    const el = box.current
    if (!el) return
    lastHeight.current = el.scrollHeight
    follow(atEnd(el))
    if (el.scrollTop <= EARLIER_PX && el.scrollHeight > el.clientHeight) earlier()
  }

  // He opened or closed something: what he looks at must not move under him.
  const onFold = useCallback(() => {
    following.current = false
    onFollowing?.(false)
    // Whether that left him at the end is known once it is drawn.
    const later = typeof requestAnimationFrame === 'function' ? requestAnimationFrame : (run: () => void) => setTimeout(run, 0)
    later(() => {
      const el = box.current
      if (!el) return
      lastHeight.current = el.scrollHeight
      follow(atEnd(el))
    })
  }, [follow, onFollowing])

  const jump = () => {
    const el = box.current
    if (!el) return
    el.scrollTop = el.scrollHeight
    lastHeight.current = el.scrollHeight
    follow(true)
    setFromKey(null)
  }

  const actions = useMemo<TranscriptActions>(() => ({ cwd, me, busy, onFold, onRun }), [cwd, me, busy, onFold, onRun])

  let body
  if (items.length === 0 && status === 'error') {
    body = (
      <div className="code-state" role="alert">
        <p>
          <strong>The session could not be read.</strong>
        </p>
        {error && (
          <p className="muted">
            {error.message} <code>{error.code}</code>
          </p>
        )}
      </div>
    )
  } else if (items.length === 0 && status === 'loading') {
    body = (
      <div className="code-state" aria-busy="true">
        <Working />
      </div>
    )
  } else if (items.length === 0) {
    body = <p className="code-state muted">Nothing yet.</p>
  } else {
    body = (
      <TranscriptContext.Provider value={actions}>
        <AskRecords.Provider value={askRecord ?? null}>
          {start > 0 && (
            <button type="button" className="button code-earlier" onClick={earlier}>
              Show earlier
            </button>
          )}
          {blocks.slice(start).map((block, i) => (
            <Fragment key={keyOf(block)}>
              {block.kind === 'group' ? <ActionGroup rows={block.rows} /> : <ItemView row={block.row} last={last.has(block.row.item.id)} />}
              {after(start + i)}
            </Fragment>
          ))}
        </AskRecords.Provider>
      </TranscriptContext.Provider>
    )
  }

  return (
    <div className="code-transcript-frame">
      {/* Focusable, so the keyboard scrolls it. */}
      <div className="code-transcript" ref={box} onScroll={onScroll} tabIndex={0} role="region" aria-label="Transcript">
        <div className="code-transcript-items" ref={content}>
          {body}
          {after(END)}
          {items.length > 0 && view.local && <p className="muted">From this Mac's runner.</p>}
          {items.length > 0 && view.offline && !view.local && <p className="muted">Offline: what this device kept of this session.</p>}
        </div>
      </div>
      {away && status === 'ready' && (
        <button type="button" className="button code-jump" onClick={jump}>
          <ChevronDownIcon size={12} />
          Jump to latest
        </button>
      )}
      <div className="visually-hidden" aria-live="polite">
        {said && <p key={said.key}>{said.text}</p>}
      </div>
    </div>
  )
}

/** A text's words, without its Markdown: what the live region reads. The document is inert: nothing in it loads. */
function plainText(markdown: string): string {
  const doc = new DOMParser().parseFromString(renderMarkdown(markdown, { localImagesOnly: true }), 'text/html')
  return (doc.body.textContent ?? '').replace(/\s+/g, ' ').trim()
}

/**
 * The agent's text that came in while the page followed the transcript, once
 * each reply is complete: when something follows it. What the transcript
 * held when it caught up is not news.
 */
function useNewText(items: readonly Item[], ready: boolean): { key: string; text: string } | null {
  const known = useRef<Set<string> | null>(null)
  const [said, setSaid] = useState<{ key: string; text: string } | null>(null)
  useEffect(() => {
    if (!ready) return
    const complete = items.slice(0, -1).filter((item): item is AssistantItem => item.kind === 'assistant')
    if (known.current === null) {
      known.current = new Set(complete.map((item) => item.id))
      return
    }
    const seen = known.current
    // Only what follows the last reply already known is news: older items filled in above are not (Poulpe-843).
    let lastSeen = -1
    complete.forEach((item, at) => {
      if (seen.has(item.id)) lastSeen = at
    })
    const fresh = complete.slice(lastSeen + 1).filter((item) => !seen.has(item.id))
    for (const item of complete) seen.add(item.id)
    if (fresh.length === 0) return
    const text = fresh
      .map((item) => plainText(item.text))
      .filter(Boolean)
      .join(' ')
    if (text) setSaid({ key: fresh.at(-1)!.id, text })
  }, [items, ready])
  return said
}
